import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fileViewUrl } from "../lib/api";
import {
  LayoutDashboard, Home, Megaphone, Users, LogOut, MapPin, ShieldCheck, UserRound, ChevronDown,
} from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["faculty", "manager", "student"] },
  { to: "/households", label: "Households", icon: Home, roles: ["faculty", "manager", "student"] },
  { to: "/awareness", label: "Awareness", icon: Megaphone, roles: ["faculty", "manager", "student"] },
  { to: "/villages", label: "Villages", icon: MapPin, roles: ["faculty", "manager", "student"] },
  { to: "/students", label: "Students", icon: Users, roles: ["faculty", "manager"] },
  { to: "/faculty", label: "Faculty", icon: ShieldCheck, roles: ["manager"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = navItems.filter((n) => n.roles.includes(user?.role));

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/75 border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="brand-logo">
            <img src="/img/acn-logo.png" alt="ACN" className="h-10 w-10 rounded-full object-cover bg-white border border-[#3B2A8C]/20" />
            <div className="leading-tight">
              <div className="font-heading font-bold text-sm text-[#3B2A8C] uppercase tracking-wide">Akal College of Nursing</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-semibold">Baru Sahib</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {items.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    isActive ? "bg-brand-primary text-white" : "text-brand-text hover:bg-brand-surfaceAlt"
                  }`
                }
              >
                <span className="inline-flex items-center gap-2">
                  <n.icon className="h-4 w-4" /> {n.label}
                </span>
              </NavLink>
            ))}
          </nav>

          <UserMenu user={user} onLogout={onLogout} />
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 pb-24 md:pb-6">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-xl border-t border-brand-border">
        <div className="flex items-stretch justify-around">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center py-3 gap-1 text-xs ${
                  isActive ? "text-brand-primary" : "text-brand-muted"
                }`
              }
            >
              <n.icon className="h-5 w-5" />
              <span className="font-medium">{n.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const initials = (user?.name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  const roleColor = user?.role === "manager" ? "#2B5F4E" : user?.role === "faculty" ? "#367661" : "#D78A76";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 h-11 pl-1 pr-2 rounded-full border border-brand-border bg-white hover:bg-brand-surfaceAlt transition-colors"
        data-testid="user-menu-btn"
      >
        {user?.photo_path ? (
          <img src={fileViewUrl(user.photo_path)} alt="" className="h-9 w-9 rounded-full object-cover" />
        ) : (
          <span className="h-9 w-9 rounded-full grid place-items-center font-heading font-semibold text-sm" style={{ background: `${roleColor}18`, color: roleColor }}>
            {initials || "?"}
          </span>
        )}
        <span className="hidden sm:flex flex-col items-start leading-tight">
          <span className="text-sm font-medium" data-testid="current-user-name">{user?.name}</span>
          <span className="text-[10px] uppercase tracking-[0.16em] text-brand-muted">{user?.role}</span>
        </span>
        <ChevronDown className="h-4 w-4 text-brand-muted hidden sm:block" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 rounded-lg border border-brand-border bg-white shadow-lg overflow-hidden z-50" data-testid="user-menu-panel">
          <div className="px-4 py-3 border-b border-brand-border bg-brand-surfaceAlt/40">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-[11px] text-brand-muted truncate">{user?.email}</div>
          </div>
          <Link
            to="/account"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-brand-surfaceAlt"
            data-testid="account-link"
          >
            <UserRound className="h-4 w-4 text-brand-muted" /> Account & Profile
          </Link>
          <button
            onClick={() => { setOpen(false); onLogout(); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-brand-surfaceAlt text-brand-danger border-t border-brand-border"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      )}
    </div>
  );
}
